/* access-repair-ui.js — page layer for /access-repair.
 *
 * Owns the uploader, the verdict card, the table list with a live preview grid, and the
 * export controls. All byte work happens in the module worker; this file never parses a
 * database itself.
 *
 * Two rules shape the flow and are worth stating because they are easy to undo:
 *   · diagnosis is free and always runs — telling somebody "your file is intact, this is a
 *     version mismatch" is a real answer and must never consume their daily allowance;
 *   · the allowance is spent when a file is DELIVERED, not when work starts.
 */
(function () {
  'use strict';

  var T = window.__ARF_ACCESS_T || {};
  var t = function (k, fb) { return T[k] || fb || k; };

  var els = {};
  var worker = null;
  var reqId = 0;
  var pending = {};
  var state = { file: null, scan: null, activeTable: null, includeDeleted: false, salvage: false, t0: 0, previewed: false };

  function $(sel, root) { return (root || document).querySelector(sel); }

  // ── telemetry ──────────────────────────────────────────────────────────────
  /* What these are for, so nobody strips them as noise later:
   *   · which VERDICT real files get — we guessed "broken header" is the common case and
   *     have no evidence; this is the only way to find out what people actually bring
   *   · which damage patterns appear, so the engine gets worked on where it matters
   *   · upload → export conversion, and where the ones who leave stop
   *   · scan time against file size on real hardware, not on this laptop
   * Slug comes from <meta name="tool-slug">; it is never passed. */
  function stage(name, meta) { try { if (window.__stage) window.__stage(name, meta); } catch (e) {} }
  function funnel(name, data) { try { if (window.__aeFunnel) window.__aeFunnel(name, data); } catch (e) {} }
  function toolError(p) { try { if (window.__aeToolError) window.__aeToolError(p); } catch (e) {} }

  // Buckets, not raw bytes: the point is "do big files work", and a raw size is closer to
  // identifying a file than a range is.
  function sizeBucket(n) {
    if (!n) return '0';
    var mb = n / 1048576;
    if (mb < 1) return '<1MB';
    if (mb < 10) return '1-10MB';
    if (mb < 50) return '10-50MB';
    if (mb < 200) return '50-200MB';
    if (mb < 600) return '200-600MB';
    return '600MB+';
  }

  // ── worker plumbing ────────────────────────────────────────────────────────

  function ensureWorker() {
    if (worker) return worker;
    worker = new Worker(window.__ACCESS_REPAIR_WORKER_URL, { type: 'module' });
    worker.onmessage = function (e) {
      var d = e.data || {};
      var p = pending[d.id];
      if (!p) return;
      // Progress shares the request id but is not the answer — the promise stays pending.
      if (d.progress) { if (p.onProgress) p.onProgress(d); return; }
      delete pending[d.id];
      if (d.error) p.reject(new Error(d.error)); else p.resolve(d.result);
    };
    worker.onerror = function (e) {
      // The failure is taken here (every pending call is rejected, the verdict says so);
      // uncancelled it would also reach window.onerror — the shared toast, Sentry. A dead
      // worker is dropped, so the next file starts a fresh one instead of waiting forever.
      if (e && e.preventDefault) e.preventDefault();
      Object.keys(pending).forEach(function (k) {
        pending[k].reject(new Error(e.message || 'worker failed'));
        delete pending[k];
      });
      try { worker.terminate(); } catch (_) {}
      worker = null;
    };
    return worker;
  }

  function call(op, payload, transfer, onProgress) {
    var id = ++reqId;
    // The worker resolves its own modules from these; see the note in the blade about why
    // they must be versioned rather than imported by a bare relative specifier.
    var msg = Object.assign({
      op: op, id: id,
      coreUrl: window.__ACCESS_CORE_URL,
      exportUrl: window.__ACCESS_EXPORT_URL,
      namesUrl: window.__ACCESS_NAMES_URL,
    }, payload || {});
    return new Promise(function (resolve, reject) {
      pending[id] = { resolve: resolve, reject: reject, onProgress: onProgress };
      ensureWorker().postMessage(msg, transfer || []);
    });
  }

  // ── progress ───────────────────────────────────────────────────────────────
  /* A database may legitimately be 2 GB, and the page sweep alone then runs for tens of
   * seconds. Without this the tab shows a frozen "Examining the file…" and people reload —
   * losing the work and starting the wait again. */
  function showProgress(label) {
    els.progress.hidden = false;
    els.progressText.textContent = label || '';
    els.progressBar.style.width = '0%';
  }
  function setProgress(done, label) {
    if (els.progress.hidden) return;
    els.progressBar.style.width = Math.round((done || 0) * 100) + '%';
    if (label) els.progressText.textContent = label;
  }
  function hideProgress() { els.progress.hidden = true; }

  // ── formatting helpers ─────────────────────────────────────────────────────

  function fmtBytes(n) {
    if (!n) return '0 ' + t('unit_b', 'B');
    var u = [t('unit_b', 'B'), t('unit_kb', 'KB'), t('unit_mb', 'MB'), t('unit_gb', 'GB')];
    var i = Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
    return (n / Math.pow(1024, i)).toFixed(i ? 1 : 0) + ' ' + u[i];
  }

  function fmtNum(n) {
    try { return Number(n).toLocaleString(window.__LOCALE || 'en'); } catch (e) { return String(n); }
  }

  function cellText(v) {
    if (v === null || v === undefined) return '';
    if (typeof v === 'boolean') return v ? '✓' : '—';
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 19).replace('T', ' ');
    return String(v);
  }

  // ── verdict ────────────────────────────────────────────────────────────────

  var VERDICT_TONE = {
    healthy: 'good', healthy_empty: 'good',
    header_lost: 'warn', partial: 'warn', truncated: 'warn', structure_lost: 'warn',
    damaged: 'bad', unreadable: 'bad', not_a_database: 'bad', empty_file: 'bad',
  };

  function renderVerdict(scan) {
    var d = scan.diagnosis;
    var tone = VERDICT_TONE[d.verdict] || 'warn';
    els.verdict.className = 'acr-verdict acr-' + tone;
    els.verdict.textContent = t('verdict_' + d.verdict, d.verdict);

    var facts = [];
    if (scan.header && scan.header.valid) {
      facts.push('<b>' + escapeHtml(scan.header.label) + '</b>');
    } else if (d.verdict !== 'not_a_database' && d.verdict !== 'empty_file') {
      facts.push('<b>' + escapeHtml(t('header_unreadable', 'header unreadable')) + '</b>');
    }
    facts.push(fmtBytes(scan.bytes));
    if (scan.pageCount) facts.push(fmtNum(scan.pageCount) + ' ' + t('pages', 'pages'));
    if (d.userTables) facts.push(fmtNum(d.userTables) + ' ' + t('tables', 'tables'));
    els.facts.innerHTML = facts.join(' · ');

    // Every issue gets a sentence in the user's own vocabulary; an unknown key would show
    // its own name, which is a bug worth seeing rather than hiding behind a generic line.
    var issues = (d.issues || []).map(function (key) {
      return '<li>' + escapeHtml(t('issue_' + key, key)) + '</li>';
    });
    els.issues.innerHTML = issues.join('');
    els.issues.hidden = !issues.length;

    // "Your file is fine" is a legitimate, useful outcome — say it plainly and stop.
    els.healthyNote.hidden = !(d.verdict === 'healthy' || d.verdict === 'healthy_empty');
    if (!els.healthyNote.hidden) els.healthyNote.textContent = t('healthy_note', '');

    renderLinked(scan);
    renderQueries(scan);
  }

  /* An Access application is usually SPLIT in two. Somebody who opens the half they click
   * every day finds no rows here, and without this panel the only conclusion available to
   * them is that the tool failed. Naming the other file turns a dead end into a next step. */
  function renderLinked(scan) {
    var linked = scan.linkedTables || [];
    els.linkedWrap.hidden = !linked.length;
    if (!linked.length) return;
    var paths = {};
    linked.forEach(function (l) { if (l.path) paths[l.path] = 1; });
    var list = Object.keys(paths);
    els.linkedTitle.textContent = t('linked_title', 'Tables that live in another file');
    els.linkedNote.textContent = t(
      scan.diagnosis.verdict === 'front_end' ? 'linked_front_end' : 'linked_some',
      ''
    ).replace(':n', fmtNum(linked.length));
    els.linkedList.innerHTML = list.map(function (p) {
      return '<li><code>' + escapeHtml(p) + '</code></li>';
    }).join('') || linked.map(function (l) {
      return '<li>' + escapeHtml(l.name) + '</li>';
    }).join('');
  }

  function renderQueries(scan) {
    var qs = scan.queries || [];
    els.queriesWrap.hidden = !qs.length;
    if (!qs.length) return;
    els.queriesTitle.textContent = t('queries_title', 'Saved queries').replace(':n', fmtNum(qs.length));
    els.queriesList.innerHTML = qs.map(function (q) {
      var flag = q.incomplete
        ? ' <span class="acr-chip acr-warn">' + escapeHtml(t('query_incomplete', 'partly rebuilt')) + '</span>'
        : '';
      return '<li><div class="acr-qname">' + escapeHtml(q.name) + flag + '</div>'
        + '<pre class="acr-qsql">' + escapeHtml(q.sql) + '</pre></li>';
    }).join('');
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  // ── table list ─────────────────────────────────────────────────────────────

  function renderTables(scan) {
    var user = scan.tables.filter(function (x) { return !x.system; });
    els.tablesWrap.hidden = !user.length;
    if (!user.length) { els.tables.innerHTML = ''; return; }

    els.tables.innerHTML = user.map(function (tb) {
      var chips = [];
      if (!tb.named) chips.push('<span class="acr-chip acr-warn">' + escapeHtml(t('chip_unnamed', 'name lost')) + '</span>');
      if (tb.structureUnknown) chips.push('<span class="acr-chip acr-warn">' + escapeHtml(t('chip_salvaged', 'text only')) + '</span>');
      if (tb.rowsClaimed && !tb.dataPages) chips.push('<span class="acr-chip acr-bad">' + escapeHtml(t('chip_no_pages', 'pages missing')) + '</span>');
      return '<label class="acr-table">' +
        '<input type="checkbox" class="acr-pick" value="' + tb.page + '" checked>' +
        '<span class="acr-tname">' + escapeHtml(tb.name) + '</span>' +
        '<span class="acr-tcols">' + tb.cols.length + ' ' + escapeHtml(t('columns', 'columns')) + '</span>' +
        '<span class="acr-tchips">' + chips.join('') + '</span>' +
        '<button type="button" class="lg-btn lg-btn-ghost lg-btn-sm acr-peek" data-page="' + tb.page + '">' +
        escapeHtml(t('btn_preview', 'Preview')) + '</button>' +
        '</label>';
    }).join('');

    Array.prototype.forEach.call(els.tables.querySelectorAll('.acr-peek'), function (b) {
      b.addEventListener('click', function () { preview(Number(b.getAttribute('data-page'))); });
    });
    if (user.length) preview(user[0].page);
  }

  // ── preview grid ───────────────────────────────────────────────────────────

  function preview(page) {
    if (!state.previewed) { state.previewed = true; stage('previewed'); }
    state.activeTable = page;
    if (els.search) els.search.value = '';
    var tb = state.scan.tables.filter(function (x) { return x.page === page; })[0];
    if (!tb) return;
    els.previewWrap.hidden = false;
    els.previewTitle.textContent = tb.name;
    els.grid.innerHTML = '<div class="acr-loading">' + escapeHtml(t('loading', 'Reading…')) + '</div>';

    call('preview', { page: page, limit: 100, includeDeleted: state.includeDeleted })
      .then(function (res) {
        drawGrid(tb, res.rows, res.deletedFlags, -1);
        renderStats(tb, res.stats);
      })
      .catch(function (err) {
        els.grid.innerHTML = '';
        if (window.__handleError) window.__handleError(err, 'access-repair preview');
      });
  }

  function drawGrid(tb, rows, deletedFlags, hitCol) {
    var head = '<tr>' + tb.colNames.map(function (c) {
      return '<th>' + escapeHtml(c) + '</th>';
    }).join('') + '</tr>';
    var body = rows.map(function (row, i) {
      var cls = (deletedFlags && deletedFlags[i]) ? ' class="acr-row-deleted"' : '';
      return '<tr' + cls + '>' + row.map(function (v, ci) {
        var mark = (Array.isArray(hitCol) ? hitCol[i] === ci : hitCol === ci) ? ' class="acr-hit"' : '';
        return '<td' + mark + '>' + escapeHtml(cellText(v)) + '</td>';
      }).join('') + '</tr>';
    }).join('');
    els.grid.innerHTML = '<table class="acr-gridtable"><thead>' + head + '</thead><tbody>' + body + '</tbody></table>';
  }

  /* Search runs over the WHOLE table in the worker, not over the hundred rows on screen.
   * "Is my invoice still in there" is the first thing anyone asks a recovery tool, and a
   * preview cannot answer it — a filter over visible rows would answer it wrongly. */
  var searchTimer = null;
  function runSearch() {
    var q = (els.search.value || '').trim();
    if (state.activeTable == null) return;
    var tb = state.scan.tables.filter(function (x) { return x.page === state.activeTable; })[0];
    if (!tb) return;
    if (!q) { els.searchInfo.textContent = ''; preview(state.activeTable); return; }

    els.searchInfo.textContent = t('searching', 'Searching…');
    call('search', { page: state.activeTable, q: q, limit: 200, includeDeleted: state.includeDeleted })
      .then(function (res) {
        drawGrid(tb, res.rows.map(function (r) { return r.row; }),
          res.rows.map(function (r) { return r.deleted; }),
          res.rows.map(function (r) { return r.col; }));
        els.searchInfo.textContent = res.matched
          ? t('search_found', 'found :n of :scanned')
            .replace(':n', fmtNum(res.matched)).replace(':scanned', fmtNum(res.scanned))
            + (res.truncated ? ' · ' + t('search_truncated', 'showing the first :n').replace(':n', fmtNum(res.rows.length)) : '')
          : t('search_none', 'nothing found in :scanned records').replace(':scanned', fmtNum(res.scanned));
        funnel('search', { matched: res.matched, scanned: res.scanned });
      })
      .catch(function (err) {
        els.searchInfo.textContent = '';
        if (window.__handleError) window.__handleError(err, 'access-repair search');
      });
  }

  /* The integrity line is the honest half of the product. Competitors report a number of
   * recovered rows; what a user needs to know is what is NOT in that number. */
  function renderStats(tb, st) {
    var bits = [];
    bits.push(fmtNum(st.rows) + ' ' + t('rows_read', 'rows read'));
    if (tb.rowsClaimed) bits.push(t('of_claimed', 'of :n recorded').replace(':n', fmtNum(tb.rowsClaimed)));
    if (st.deleted) bits.push(fmtNum(st.deleted) + ' ' + t('deleted_recovered', 'deleted recovered'));
    if (st.reclaimed) bits.push(fmtNum(st.reclaimed) + ' ' + t('deleted_overwritten', 'deleted, content already overwritten'));
    if (st.badRows) bits.push(fmtNum(st.badRows) + ' ' + t('rows_unreadable', 'unreadable'));
    if (st.badPages) bits.push(fmtNum(st.badPages) + ' ' + t('pages_damaged', 'damaged pages'));
    /* Ticking "show deleted" on a table that has none changed nothing and said nothing, so
     * the control read as broken. Silence is not an answer — name the outcome. */
    if (state.includeDeleted && !st.deleted && !st.reclaimed) {
      bits.push(t('no_deleted_here', 'no deleted records in this table'));
    }
    els.stats.textContent = bits.join(' · ');
  }

  /** True when the scan found real damage — i.e. when a repair actually happened. */
  function neededRepair(scan) {
    return !!scan && !/^healthy/.test(scan.diagnosis.verdict);
  }

  // ── scanning ───────────────────────────────────────────────────────────────

  function analyse(file) {
    state.file = file;
    state.t0 = Date.now();
    state.previewed = false;
    stage('file_received', { size: sizeBucket(file && file.size) });
    /* `.active` is not decoration. app.css carries a site-wide rule —
     *   [class$="-workspace"]:not(.active) > :not(.upload-zone) { display: none }
     * — so ANY element whose class ends in "-workspace" hides every child until it is
     * marked active. Clearing `hidden` alone leaves the whole panel rendered but collapsed:
     * the DOM is fully populated, textContent reads fine, and nothing is on screen. */
    els.workspace.hidden = false;
    els.workspace.classList.add('active');
    els.verdict.className = 'acr-verdict';
    els.verdict.textContent = t('diagnosing', 'Examining the file…');
    els.facts.textContent = '';
    els.issues.hidden = true;
    els.tablesWrap.hidden = true;
    els.previewWrap.hidden = true;
    els.exportRow.hidden = true;
    els.freeNote.hidden = true;
    stage('scanning');

    /* An engine without module workers (Firefox < 114, Safari < 15) starts the reader as a
     * classic script, where its import() of the engine is refused — and the scan ended in
     * «No database structures found» about a perfectly good file (measured on an emulated
     * engine, 08.10.2026). The engine is asked first; the verdict says what is true. */
    if (window.__moduleWorkerOk && !window.__moduleWorkerOk()) {
      els.verdict.className = 'acr-verdict acr-bad';
      els.verdict.textContent = t('engine_too_old', 'This browser cannot read Access databases.');
      toolError({ stage: 'engine', message: 'engine_too_old_module_worker' });
      stage('engine_too_old');
      return;
    }

    showProgress(t('diagnosing', 'Examining the file…'));
    file.arrayBuffer().then(function (buf) {
      return call('scan', { buffer: buf, salvage: state.salvage, baseName: file.name }, [buf], function (p) {
        setProgress(p.done);
      });
    }).then(function (scan) {
      /* A file whose typed tables are gone but whose pages still hold rows deserves the
       * deep pass automatically — asking the user to find a checkbox first would hide the
       * only thing that can still help them. */
      var d = scan.diagnosis;
      var needSalvage = !state.salvage && (d.orphanDataPages > 0) &&
        (d.verdict === 'structure_lost' || d.verdict === 'unreadable' || d.tablesWithData === 0);
      if (needSalvage) {
        state.salvage = true;
        return call('scan', { salvage: true });
      }
      return scan;
    }).then(function (scan) {
      hideProgress();
      state.scan = scan;
      /* Rendering is fenced off from reading. A crash in our own drawing code must never
       * be reported as a verdict on the user's file — an earlier version let a null element
       * throw here and the catch below announced "no database structures found" about a
       * database it had just parsed perfectly. */
      try {
        renderVerdict(scan);
        renderTables(scan);
        var hasData = scan.tables.some(function (x) { return !x.system; });
        els.exportRow.hidden = !hasData;
        /* Say out loud that an intact file is on the house, and re-enable the button the
         * cap component may have dimmed: a run we do not charge for must not be gated by a
         * counter it never touches. */
        var free = hasData && !neededRepair(scan);
        els.freeNote.hidden = !free;
        if (free && els.exportBtn) {
          els.exportBtn.disabled = false;
          els.exportBtn.removeAttribute('aria-disabled');
        }
      } catch (renderErr) {
        if (window.__handleError) window.__handleError(renderErr, 'access-repair render');
        toolError({ stage: 'render', message: String(renderErr && renderErr.message || renderErr) });
      }

      var d = scan.diagnosis;
      stage('verdict_' + d.verdict);
      /* One event per distinct damage pattern, not just the headline verdict: a file can be
       * truncated AND missing its catalog, and knowing which combinations occur is what
       * decides where the engine gets improved next. */
      if (window.__aeDiagnostic) {
        window.__aeDiagnostic({ issue: d.verdict });
        (d.issues || []).forEach(function (i) { window.__aeDiagnostic({ issue: 'damage_' + i }); });
      }
      funnel('scan_done', {
        verdict: d.verdict,
        family: (scan.header && scan.header.family) || 'unknown',
        header_ok: !!(scan.header && scan.header.valid),
        catalog_ok: !!scan.catalogFound,
        tables: d.userTables,
        tables_with_data: d.tablesWithData,
        rows_claimed: d.rowsClaimed,
        salvaged: scan.salvagedTables || 0,
        size: sizeBucket(scan.bytes),
        pages: scan.pageCount,
        ms: Date.now() - state.t0,
      });
    }).catch(function (err) {
      hideProgress();
      els.verdict.className = 'acr-verdict acr-bad';
      els.verdict.textContent = t('verdict_unreadable', 'Could not read this file');
      if (window.__handleError) window.__handleError(err, 'access-repair scan');
      toolError({ stage: 'scan', message: String(err && err.message || err) });
      stage('scan_failed');
    });
  }

  // ── export ─────────────────────────────────────────────────────────────────

  function selectedPages() {
    return Array.prototype.map.call(els.tables.querySelectorAll('.acr-pick:checked'), function (i) {
      return Number(i.value);
    });
  }

  /* Writing straight into a chosen folder, where the browser allows it. A database with
   * forty tables otherwise arrives as a zip the user then has to unpack, and on the large
   * files this tool exists for that zip is a second full copy held in memory.
   * Feature-detected, never assumed: Safari and Firefox have no such picker. */
  function canSaveToFolder() {
    return typeof window.showDirectoryPicker === 'function' && window.isSecureContext;
  }

  function doExport(toFolder) {
    var pages = selectedPages();
    if (!pages.length) {
      if (window.__toast) window.__toast(t('err_pick_table', 'Choose at least one table'), 'warning');
      // A guard, not a crash: keeps tool_error meaningful and still shows up in dashboards.
      if (window.__aeBlocked) window.__aeBlocked({ reason: 'no_tables_selected' });
      return;
    }
    var fmt = els.format.value;
    var limit = window.__toolLimit;
    var ticket = null;
    var charge = neededRepair(state.scan);
    var t1 = Date.now();

    els.exportBtn.disabled = true;
    if (els.folderBtn) els.folderBtn.disabled = true;
    els.exportBtn.textContent = t('exporting', 'Preparing…');
    stage('exporting', { format: fmt, to_folder: !!toFolder });
    showProgress(t('exporting', 'Preparing…'));

    var dirHandle = null;
    Promise.resolve()
      .then(function () {
        /* Ask for the folder BEFORE any work: the picker needs the click's user activation,
         * and a dismissed picker must cost nothing — not the allowance, not the wait. */
        if (!toFolder) return null;
        return window.showDirectoryPicker({ mode: 'readwrite', id: 'access-repair' })
          .then(function (h) { dirHandle = h; })
          .catch(function () { throw new Error('__cancelled__'); });
      })
      .then(function () {
        /* An intact file is not a repair, so it does not spend the day's allowance — the
         * project rule is explicit that "the repair was not needed" ends in abort(), not
         * commit(). Reading an undamaged database out to Excel costs us nothing anyway.
         *
         * One unit of work = one recovered database, keyed on the file, so re-exporting the
         * same database in another format on the same day is not charged twice either. */
        if (!limit || !charge) return null;
        return limit.begin('access-repair', [state.file ? (state.file.name + ':' + state.file.size) : 'db']);
      })
      .then(function (tk) {
        ticket = tk;
        return call('export', {
          format: fmt, pages: pages, includeDeleted: state.includeDeleted,
          separator: els.separator ? els.separator.value : ',',
          baseName: state.file ? state.file.name : 'recovered',
          fflateUrl: window.__FFLATE_URL,
          asFiles: !!dirHandle,
          includeQueries: !!(state.scan && state.scan.queries && state.scan.queries.length),
        }, [], function (p) { setProgress(p.done); });
      })
      .then(function (res) {
        if (res.kind === 'files') return writeToFolder(dirHandle, res).then(function () { return res; });
        var blob = res.bytes
          ? new Blob([res.bytes], { type: 'application/octet-stream' })
          : new Blob([res.text], { type: 'text/plain;charset=utf-8' });
        window.__downloadFile(blob, res.name);
        return res;
      })
      .then(function (res) {
        // Only a delivered file is charged. A failure or a cancel must not cost anything.
        if (ticket && ticket.commit) ticket.commit();
        if (window.__toast) {
          window.__toast(t('saved', 'Saved :n rows from :t tables')
            .replace(':n', fmtNum(res.total.rows)).replace(':t', fmtNum(res.total.tables)), 'success');
        }
        stage('saved', { format: fmt });
        if (window.__aeToolComplete) {
          window.__aeToolComplete({
            format: fmt,
            tables: res.total.tables,
            rows: res.total.rows,
            deleted: res.total.deleted,
            verdict: state.scan ? state.scan.diagnosis.verdict : null,
            charged: charge,
            previewed: state.previewed,
            size: sizeBucket(state.file && state.file.size),
            export_ms: Date.now() - t1,
            total_ms: Date.now() - state.t0,
          });
        }
      })
      .catch(function (err) {
        if (ticket && ticket.abort) ticket.abort();
        // Dismissing the folder picker is a decision, not a failure: no toast, no error
        // event, and above all no charge.
        if (String(err && err.message) === '__cancelled__') { stage('export_cancelled'); return; }
        if (window.__handleError) window.__handleError(err, 'access-repair export');
        toolError({ stage: 'export', format: fmt, message: String(err && err.message || err) });
        stage('export_failed', { format: fmt });
      })
      .then(function () {
        hideProgress();
        els.exportBtn.disabled = false;
        if (els.folderBtn) els.folderBtn.disabled = false;
        els.exportBtn.textContent = t('btn_export', 'Save data');
      });
  }

  function writeToFolder(dir, res) {
    var i = 0;
    return res.files.reduce(function (chain, f) {
      return chain.then(function () {
        return dir.getFileHandle(f.name, { create: true })
          .then(function (fh) { return fh.createWritable(); })
          .then(function (w) {
            return Promise.resolve(w.write(f.text != null ? f.text : f.bytes)).then(function () { return w.close(); });
          })
          .then(function () {
            if (window.__deliveredToDisk) window.__deliveredToDisk(f.name, f.bytes || (f.text != null ? new Blob([f.text]) : 0), 'folder');
            setProgress(++i / res.files.length);
          });
      });
    }, Promise.resolve());
  }

  // ── wiring ─────────────────────────────────────────────────────────────────

  function init() {
    els.root = $('.acr-root');
    if (!els.root) return;
    els.workspace = $('.acr-workspace', els.root);
    els.verdict = $('.acr-verdict', els.root);
    els.facts = $('.acr-facts', els.root);
    els.issues = $('.acr-issues', els.root);
    els.healthyNote = $('.acr-healthy', els.root);
    els.tablesWrap = $('.acr-tables-wrap', els.root);
    els.tables = $('.acr-tables', els.root);
    els.previewWrap = $('.acr-preview-wrap', els.root);
    els.previewTitle = $('.acr-preview-name', els.root);
    els.grid = $('.acr-grid', els.root);
    els.stats = $('.acr-stats', els.root);
    els.exportRow = $('.acr-export', els.root);
    els.freeNote = $('.acr-free', els.root);
    els.progress = $('.acr-progress', els.root);
    els.progressBar = $('.acr-bar', els.root);
    els.progressText = $('.acr-progress-text', els.root);
    els.linkedWrap = $('.acr-linked-wrap', els.root);
    els.linkedTitle = $('.acr-linked-title', els.root);
    els.linkedNote = $('.acr-linked-note', els.root);
    els.linkedList = $('.acr-linked-list', els.root);
    els.queriesWrap = $('.acr-queries-wrap', els.root);
    els.queriesTitle = $('.acr-queries-title', els.root);
    els.queriesList = $('.acr-queries-list', els.root);
    els.search = $('#acrSearch');
    els.searchInfo = $('.acr-search-info', els.root);
    els.folderBtn = $('#acrFolderBtn');
    els.exportBtn = $('#acrExportBtn');
    els.format = $('#acrFormat');
    els.separator = $('#acrSeparator');
    els.deleted = $('#acrDeleted');
    els.reset = $('#acrResetBtn');

    /* __bindUploaderFile, not a raw listener on 'tool-uploader:file'. On a slow connection
     * the per-tool script can attach AFTER the user has already picked a file, and the
     * event is simply lost — the tool then sits dead until they pick it again. The helper
     * replays a pending file, and a phpunit test flags any blade that hand-rolls this. */
    var zone = document.getElementById('acrDrop');
    if (zone && window.__bindUploaderFile) window.__bindUploaderFile(zone, analyse);

    if (els.exportBtn) els.exportBtn.addEventListener('click', function () { doExport(false); });
    if (els.folderBtn) {
      // Shown only where the picker exists, rather than offered and then failing.
      els.folderBtn.hidden = !canSaveToFolder();
      els.folderBtn.addEventListener('click', function () { doExport(true); });
    }
    if (els.search) {
      // Debounced: each keystroke would otherwise walk the whole table.
      els.search.addEventListener('input', function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(runSearch, 300);
      });
      els.search.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { clearTimeout(searchTimer); runSearch(); }
      });
    }
    if (els.format) {
      window.__toolSettings.bind('access-repair', els.format, 'format', 'xlsx');
      els.format.addEventListener('change', function () {
        if (els.separator) els.separator.parentNode.hidden = els.format.value !== 'csv';
      });
      if (els.separator) els.separator.parentNode.hidden = els.format.value !== 'csv';
    }
    if (els.separator) window.__toolSettings.bind('access-repair', els.separator, 'separator', ',');
    if (els.deleted) {
      els.deleted.addEventListener('change', function () {
        state.includeDeleted = els.deleted.checked;
        if (state.activeTable != null) preview(state.activeTable);
      });
    }
    if (els.reset) {
      els.reset.addEventListener('click', function () {
        state = { file: null, scan: null, activeTable: null, includeDeleted: false, salvage: false };
        els.workspace.hidden = true;
        els.workspace.classList.remove('active');
        // Frees the database bytes held in the worker; without it a second file would sit
        // alongside the first for the lifetime of the tab.
        call('release', {}).catch(function () {});
      });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
